import { Progress } from '@components'
import React, { useCallback } from 'react'
import { formatDate } from '@utils'
import styled from 'styled-components'

interface Props {
  titleFrom: string
  dateFrom: Date
  titleTo: string
  dateTo: Date
  progressColor?: (percent: number) => string
  appearance?: 'card' | 'flat'
  initialAnimation?: boolean
  className?: string
}

export const DateProgressItem: React.FC<Props> = ({
  titleFrom,
  titleTo,
  dateFrom,
  dateTo,
  progressColor,
  appearance = 'card',
  initialAnimation = false,
  className
}) => {
  const getPercent = useCallback(() => {
    const start = new Date(dateFrom).setHours(0, 0, 0, 0)
    const end = new Date(dateTo).setHours(23, 59, 59, 999)
    const now = Date.now()

    return Math.max(Math.min(Math.floor(((now - start) / (end - start)) * 100), 100), 0)
  }, [dateFrom, dateTo])

  const percent = getPercent()

  return (
    <Container className={className} $appearance={appearance}>
      <Row>
        <Column>
          <TitleText $alignSelf="flex-start">{titleFrom}</TitleText>
          <DateText $alignSelf="flex-start">{formatDate(dateFrom)}</DateText>
        </Column>
        <Column>
          <TitleText $alignSelf="flex-end">{titleTo}</TitleText>
          <DateText $alignSelf="flex-end">{formatDate(dateTo)}</DateText>
        </Column>
      </Row>
      <Row>
        <Progress
          percent={percent}
          progressColor={progressColor?.(percent)}
          initialAnimation={initialAnimation}
        />
      </Row>
    </Container>
  )
}

const Container = styled.div<{ $appearance: 'card' | 'flat' }>`
  display: flex;
  flex: 1;
  position: relative;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-direction: column;
  max-width: 100%;
  padding: ${({ $appearance }) => ($appearance === 'flat' ? 0 : '16px')};
  border-radius: ${({ $appearance }) => ($appearance === 'flat' ? 0 : '1rem')};
  background: ${({ $appearance }) => ($appearance === 'flat' ? 'transparent' : '#fff')};
  box-shadow: ${({ $appearance }) =>
    $appearance === 'flat' ? 'none' : '0 1px 4px rgba(0, 0, 0, 0.1)'};
`

const Row = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
`

const TitleText = styled.h4<{ $alignSelf?: string }>`
  margin: 0;
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  align-self: ${({ $alignSelf }) => $alignSelf || 'flex-start'};
`

const DateText = styled.span<{ $alignSelf?: string }>`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  align-self: ${({ $alignSelf }) => $alignSelf || 'flex-start'};
`
